import React, { useState, useEffect, useMemo } from "react";
import Calendar from "react-calendar";
import styles from "./Calendar.module.css";
import { jwtDecode } from "jwt-decode";
import api from "../../api/axios";
import { LuSearch, LuPlus, LuX, LuArrowLeft } from "react-icons/lu";
import { FaStar } from "react-icons/fa";
import Dropdown from "../../components/Dropdown/Dropdown";
import { ConfirmationModal } from "../../components/ConfirmationModal/ConfirmationModal";

// ---------- Utilities ----------
const convertTimeToNumber = (time) => {
  if (!time) return 0;
  const t = time.toUpperCase();
  let [h, m] = t.replace(/AM|PM/, "").split(":").map(Number);
  if (t.includes("PM") && h !== 12) h += 12;
  if (t.includes("AM") && h === 12) h = 0;
  return h * 60 + m;
};

const toYmdLocal = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

const daysInMonth = (year, month) => new Date(year, month, 0).getDate();

const filterAndSortEvents = (events, filters, search, sortBy) => {
  const q = search.toLowerCase();

  return events
	  .map(ev => ({
      ...ev,
      category: ev.category || "Unranked",
    }))
    .filter((ev) => filters.includes(ev.category))
    .filter((ev) => ev.name.toLowerCase().includes(q))
    .sort((a, b) => {
      if (sortBy === "time")
        return convertTimeToNumber(a.time) - convertTimeToNumber(b.time);
      if (sortBy === "name") return a.name.localeCompare(b.name);
      if (sortBy === "category") return a.category.localeCompare(b.category);
      return 0;
    });
};

const FromStars = (stars) => {
  const s = Number(stars) || 0;
  if (s >= 4) return "High";
  if (s >= 2) return "Medium";
  if (s === 1) return "Low";
  return "Unranked";
};

const CATEGORY_OPTIONS = [
  { value: "High",     label: "4-5" },
  { value: "Medium",   label: "2-3" },
  { value: "Low",      label: "1" },
  { value: "Unranked", label: "Unranked" },
];	

const CATEGORY_DISPLAY = {
  High:     { label: "4-5",      showStar: true },
  Medium:   { label: "2-3",      showStar: true },
  Low:      { label: "1",        showStar: true },
  Unranked: { label: "Unranked", showStar: false },
};

// ---------- Component ----------
function EventPage() {
  // UI & filtering
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilters, setSelectedFilters] = useState(["High", "Medium", "Low", "Unranked"]);
  const [sortBy, setSortBy] = useState("time");
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Players and name selection modal
  const [players, setPlayers] = useState([]);
  const [nameInput, setNameInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showNameModal, setShowNameModal] = useState(false);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [nameError, setNameError] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);

  // Add-event modal
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [playerName, setPlayerName] = useState("");
  const [playerLocation, setPlayerLocation] = useState("");
  const [locationError, setLocationError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Time selection
  const [hour, setHour] = useState(1);
  const [minute, setMinute] = useState(0);
  const [ampm, setAmPm] = useState("AM");
  const playerTime = `${hour}:${String(minute).padStart(2, "0")}${ampm}`;

  // Date selection in modal
  const today = new Date();
  const [eventYear, setEventYear] = useState(today.getFullYear());
  const [eventMonth, setEventMonth] = useState(today.getMonth() + 1);
  const [eventDay, setEventDay] = useState(today.getDate());

  // Events
  const [gamesList, setGamesList] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");

  // For delete modal
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [user_id, setUserId] = useState(null);
  const [monthCategoryMap, setMonthCategoryMap] = useState({});

  const [activeStartDate, setActiveStartDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  // Map: player_id -> stars (for quick lookup)
  const playerStarMap = useMemo(() => {
    const map = new Map();
    players.forEach((p) => {
      map.set(p.player_id, p.stars ?? 0);
    });
    return map;
  }, [players]);

  // Load user ID
  useEffect(() => {
    const token = localStorage.getItem("jwtToken");
    if (!token) return;

    try {
      const decoded = jwtDecode(token);
      setUserId(decoded.user_id);
    } catch (err) {
      console.error("JWT decode failed", err);
    }
  }, []);

  // Load players
  useEffect(() => {
    if (!user_id) return;

    const loadPlayers = async () => {
      try {
        setLoadingPlayers(true);
        const res = await api.get(`/players/${user_id}`);
        setPlayers(res.data || []);
      } catch (e) {
        setPlayers([]);
      } finally {
        setLoadingPlayers(false);
      }
    };

    loadPlayers();
  }, [user_id]);

  // Load player suggestion list for searching
  useEffect(() => {
    const q = nameInput.trim().toLowerCase();
    if (!q) {
      setSuggestions([]);
      return;
    }

    setSuggestions(
      players
        .filter((p) => `${p.first_name} ${p.last_name}`.toLowerCase().includes(q))
        .slice(0, 8)
    );

    setActiveIndex(-1);
  }, [nameInput, players]);

  // Update day when month/year changes
  useEffect(() => {
    const max = daysInMonth(eventYear, eventMonth);
    if (eventDay > max) setEventDay(max);
  }, [eventMonth, eventYear]);

  // Update events and stars
	useEffect(() => {
    if (!user_id || !activeStartDate) return;
    fetchMonthSummary(activeStartDate);
  }, [user_id, activeStartDate, players]);

	// Fetch month summary (for calendar dots)
  const fetchMonthSummary = async (baseDate) => {
    if (!baseDate) return;

    const year = baseDate.getFullYear();
    const month = baseDate.getMonth() + 1;

    try {
      const res = await api.get("/games-month-summary", {
        params: { year, month },
      });

      const rows = Array.isArray(res.data) ? res.data : [];

      const newMap = {};

      for (const row of rows) {
        const dateStr = row.date;

        // Skip stale backend entries for deleted players
        if (!playerStarMap.has(row.player_id)) {
          continue;
        }

        const stars = playerStarMap.get(row.player_id) ?? row.stars ?? 0;
        const category = FromStars(stars);

        if (!newMap[dateStr]) {
          newMap[dateStr] = {
            High: false,
            Medium: false,
            Low: false,
            Unranked: false,
          };
        }

        newMap[dateStr][category] = true;
      }

      setMonthCategoryMap(newMap);
    } catch (err) {
      console.error("Month summary fetch failed", err);
    }
  };


  // Fetch events
	const fetchGames = async () => {
		if (!user_id) return;

		setErrorMsg("");
		setGamesList([]);

		const dateStr = toYmdLocal(selectedDate);

		try {
			// Use optimized API that returns all players + stars in one call
			const res = await api.get("/games-by-date-full", {
				params: { date: dateStr, user_id },
			});
			const rows = Array.isArray(res.data) ? res.data : [];

			if (rows.length === 0) {
				setErrorMsg("No games found for this date");
				return;
			}

			const formatted = rows.map((row) => {
				const category = FromStars(row.stars);
				return {
					id: row.player_id,
					gameId: row.game_id,
					name: `${row.first_name} ${row.last_name}`,
					time: row.game_time,
					location: row.location,
					date: row.game_date,
          rating: row.stars,
					category, // stars-based category
				};
			});

			setGamesList(formatted);
		} catch (err) {
			if (err.response && err.response.status === 404) {
				setErrorMsg("No games found for this date");
			} else {
				setErrorMsg("Failed to fetch games");
				console.error(err);
			}
		}
};

	useEffect(() => {
		fetchGames();
	}, [selectedDate, user_id]);

  // Update event list based on filter
  const visibleEvents = filterAndSortEvents(
    gamesList,
    selectedFilters,
    searchQuery,
    sortBy
  );

  const eventCategoryMap = useMemo(() => {
    const map = {};

    for (const [date, cats] of Object.entries(monthCategoryMap)) {
      const hasAnySelected = selectedFilters.some(
        (cat) => cats[cat]
      );
      if (!hasAnySelected) continue;

      map[date] = {
        High: selectedFilters.includes("High") && cats.High,
        Medium: selectedFilters.includes("Medium") && cats.Medium,
        Low: selectedFilters.includes("Low") && cats.Low,
        Unranked: selectedFilters.includes("Unranked") && cats.Unranked,
      };
    }

    return map;
  }, [monthCategoryMap, selectedFilters]);

  const eventDates = useMemo(() => {
    return new Set(Object.keys(eventCategoryMap));
  }, [eventCategoryMap]);

  // Add event flow
  const openAddEventFlow = () => {
    setNameInput("");
    setNameError("");
    setShowNameModal(true);

    // Pre-fill modal date with calendar date
    setEventYear(selectedDate.getFullYear());
    setEventMonth(selectedDate.getMonth() + 1);
    setEventDay(selectedDate.getDate());
  };

  // Switch to add event modal
  const handleNameContinue = (overrideName) => {
    const input = (overrideName || nameInput).trim();
    if (!input) {
      setNameError("Please enter a name.");
      return;
    }

    const match = players.find(
      (p) => `${p.first_name} ${p.last_name}`.toLowerCase() === input.toLowerCase()
    );

    if (!match) {
      setNameError("Player not found");
      return;
    }

    setPlayerName(`${match.first_name} ${match.last_name}`);
    setShowNameModal(false);
    setShowAddEventModal(true);
  };

  // Reset form for switching back to searchName from addEvent
  const resetForm = () => {
    setHour(1);
    setMinute(0);
    setAmPm("AM");
    setNameInput("");
    setPlayerName("");
    setPlayerLocation("");
    setNameError("");
    setLocationError("");
    setSuggestions([]);
    setActiveIndex(-1);
  };

  // Add event
  const handleAddEvent = async () => {
    if (!playerLocation.trim()) {
      setLocationError("Please enter a location.");
      return;
    }

    setLocationError("");
    setIsSaving(true);

    const dateStr = `${eventYear}-${String(eventMonth).padStart(
      2,
      "0"
    )}-${String(eventDay).padStart(2, "0")}`;

    try {
      await api.post("/games", {
        name: playerName,
        time: playerTime,
        location: playerLocation,
        date: dateStr,
      });

      setShowAddEventModal(false);
      fetchGames();

      const monthStart = new Date(eventYear, eventMonth - 1, 1);
      fetchMonthSummary(monthStart);
    } catch (err) {
      setErrorMsg("Failed to create event");
    } finally {
      setIsSaving(false);
    }
  };

  // Delete Event
  const handleDeleteEvent = async (gameId, playerId) => {
    try {
      await api.delete(`/game/delete-event/${gameId}/${playerId}`);
      fetchGames();

      const monthStart = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        1
      );
      fetchMonthSummary(monthStart);
    } catch (err) {
      alert("Failed to delete event");
    }
  };

  /* Display stars based on player rating */
  const starDisplay = (rating) => {
    return rating > 0 ?
      [...Array(rating)].map((_, i) => (
        <FaStar
          className={styles.starRating}
          key={i}
          color={"#ff3d00"}
        />
      )) : "Unranked"
  };


  return (
    <div className={styles.calendarPage}>
      <div className={styles.header}>
        <h1>Calendar</h1>
        <p>Track and manage your recruits' games</p>
      </div>

      <div className={styles.mainContent}>
        {/* Left section */}
        <div className={styles.leftSection}>
          {/* Search + Add */}
          <div className={styles.searchContainer}>
            <input
              type="text"
              placeholder="Search events..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchBar}
            />
            <LuSearch className={styles.searchIcon} />

            <button className={styles.addEventButton} onClick={openAddEventFlow}>
              <LuPlus className={styles.addIcon} />
              Add Event
            </button>
          </div>

          {/* Event List */}
          <div className={styles.eventContainer}>
            <div className={styles.eventListHeader}>
              <h2>{selectedDate.toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric"
                })}
              </h2>
              <div className={styles.sortOptions}>
                <label>Sort By:</label>
                <button onClick={() => setSortBy("time")}>Time</button>
                <button onClick={() => setSortBy("name")}>Name</button>
                <button onClick={() => setSortBy("category")}>Category</button>
              </div>
            </div>

            <div className={styles.eventList}>
							{visibleEvents.length ? (
								visibleEvents.map((ev) => {
									const cfg = CATEGORY_DISPLAY[ev.category] || CATEGORY_DISPLAY.Unranked;

									return (
										<div
											key={`${ev.gameId}-${ev.id}-${ev.time}`}
											className={styles.eventCard}
										>
											<div>
												<h3>
                          {ev.name}{" "}
                        </h3>
												<p>
													{ev.time},{" "}
													{ev.location},{" "}
                          {starDisplay(ev.rating) /* Display individual player ratings */} 
												</p>
											</div>

											<button
												className={styles.deleteButton}
												onClick={() =>
													setDeleteTarget({ gameId: ev.gameId, playerId: ev.id })
												}
											>
												<LuX />
											</button>
										</div>
									);
								})
							) : (
								<div className={styles.eventErrorCard}>
									<h3>{errorMsg || "Loading..."}</h3>
								</div>
							)}
						</div>
          </div>
        </div>

        {/* Right Section */}
        <div className={styles.rightSection}>
          <Calendar 
            onChange={setSelectedDate} 
            value={selectedDate} 
            locale="en-US" 

            onActiveStartDateChange={({ activeStartDate, view }) => {
              if (view === "month" && activeStartDate) {
                setActiveStartDate(activeStartDate);
              }
            }}

            tileClassName={({ date, view }) => {
              if (view !== "month") return "";
              const ymd = toYmdLocal(date);
              return eventDates.has(ymd) ? styles.hasEvents : "";
            }}
						tileContent={({ date, view }) => {
							if (view !== "month") return null;

							const ymd = toYmdLocal(date);
							const cats =
								eventCategoryMap[ymd] || { High: false, Medium: false, Low: false, Unranked: false };

							return (
								<div className={styles.dotContainer}>
									{cats.High && (
										<span className={`${styles.dot} ${styles.highDot}`} />
									)}
									{cats.Medium && (
										<span className={`${styles.dot} ${styles.mediumDot}`} />
									)}
									{cats.Low && (
										<span className={`${styles.dot} ${styles.lowDot}`} />
									)}
									{cats.Unranked && (
										<span className={`${styles.dot} ${styles.unrankedDot}`} />
									)}
								</div>
							);
						}}
          />

          {/* Sorting + Filters */}
          <div className={styles.controls}>
            <div className={styles.filters}>
              {CATEGORY_OPTIONS.map((opt) => {
                const val = opt.value;

                const dotClass =
                  val === "High"
                    ? styles.highDot
                    : val === "Medium"
                    ? styles.mediumDot
                    : val === "Low"
                    ? styles.lowDot
                    : styles.unrankedDot;

                return (
                  <label key={val}>
                    <div className={styles.filterLabelLeft}>
                      <input
                        type="checkbox"
                        checked={selectedFilters.includes(val)}
                        onChange={() =>
                          setSelectedFilters((prev) =>
                            prev.includes(val)
                              ? prev.filter((f) => f !== val)
                              : [...prev, val]
                          )
                        }
                      />
                      <span className={styles.filterLabelText}>
                        {val !== "Unranked" && (
                          <FaStar className={styles.filterStar} />
                        )}
                        {opt.label}
                      </span>
                    </div>

                    <span className={`${styles.dot} ${dotClass}`} />
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Name Modal */}
      {showNameModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h3>Add Event</h3>
            <div className={styles.searchPlayerContainer}>
              <Dropdown
                mode="search"
                inputValue={nameInput}
                onInputChange={(v) => {
                  setNameInput(v);
                  setNameError("");
                }}
                suggestions={suggestions}
                loading={loadingPlayers}
                showSuggestions={suggestions.length > 0}
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                onSelect={(p) =>
                  handleNameContinue(`${p.first_name} ${p.last_name}`)
                }
                onEnterWithoutSelection={() => handleNameContinue(nameInput)}
              />
            </div>

            {nameError && <div className={styles.warning}>{nameError}</div>}

            <div className={styles.searchPlayerButtonContainer}>
              <button 
                onClick={() => setShowNameModal(false)}
                className={styles.cancelButton}
              >
                Cancel
              </button>
              <button 
                onClick={() => handleNameContinue(nameInput)} 
                disabled={!nameInput.trim()}
                className={styles.continueButton}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Event Modal */}
      {showAddEventModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <button
                onClick={() => {
                  setShowAddEventModal(false);
                  resetForm();
                  setShowNameModal(true);
                }}
                className={styles.backButton}
              >
                <LuArrowLeft className={styles.backButtonIcon}/>
              </button>
              <h3>Add Event</h3>
            </div>

            {/* Player */}
            <h4>Player:</h4>
            <div className={styles.fieldContainer}>
              <input 
                type="text" 
                value={playerName} 
                disabled 
                className={styles.field} 
              />
            </div>

            {/* Date */}
            <h4>Select Date:</h4>
            <div className={styles.fieldContainer}>
              <Dropdown
                mode="select"
                value={eventMonth}
                onChange={(v) => setEventMonth(Number(v))}
                options={[...Array(12)].map((_, i) => String(i + 1).padStart(2, "0"))}
              />

              <Dropdown
                mode="select"
                value={eventDay}
                onChange={(v) => setEventDay(Number(v))}
                options={[...Array(daysInMonth(eventYear, eventMonth))].map((_, i) =>
                  String(i + 1).padStart(2, "0")
                )}
              />

              <Dropdown
                mode="select"
                value={eventYear}
                onChange={(v) => setEventYear(Number(v))}
                options={[...Array(20)].map((_, i) => today.getFullYear() + i)}
              />
            </div>

            {/* Time */}
            <h4>Select Time:</h4>
            <div className={styles.fieldContainer}>
              <Dropdown
                mode="select"
                value={hour}
                onChange={(v) => setHour(Number(v))}
                options={[...Array(12)].map((_, i) => i + 1)}
              />

              <Dropdown
                mode="select"
                value={String(minute).padStart(2, "0")} // Convert 0 -> "00"
                onChange={(v) => setMinute(Number(v))}
                options={[...Array(12)].map((_, i) => String(i * 5).padStart(2, "0"))}
              />

              <Dropdown
                mode="select"
                value={ampm}
                onChange={setAmPm}
                options={["AM", "PM"]}
              />
            </div>

            {/* Location */}
            <h4>Location: <span style={{ color: "red" }}>*</span> </h4>
            <div className={styles.fieldContainer}>
              <input
                type="text"
                value={playerLocation}
                onChange={(e) => {
                  setPlayerLocation(e.target.value);
                  setLocationError("");
                }}
                className={`${styles.field} ${locationError ? styles.errorField : ""}`}
              />
            </div>

            {locationError && <div className={styles.warning}>{locationError}</div>}
              
            {/* Buttons */}
            <div className={styles.buttonContainer}>
              <div>
                <button 
                  onClick={() => setShowAddEventModal(false)}
                  className={styles.cancelButton}
                >
                  Cancel
                </button>
              </div>
              <button 
                onClick={handleAddEvent} 
                disabled={isSaving}
                className={styles.continueButton}
              >
                {isSaving ? "Saving..." : "Save Event"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Event Confirmation Modal */}
      {deleteTarget && (
        <ConfirmationModal
          onConfirm={() => {
            handleDeleteEvent(deleteTarget.gameId, deleteTarget.playerId);
            setDeleteTarget(null);
          }}
          onCancel={() => setDeleteTarget(null)}
          message={"Are you sure you want to delete this event?"}
        />
      )}
    </div>
  );
}

export default EventPage;
